import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Receipt } from 'lucide-react'

import { financeApi } from '@/services/finance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { Expense } from '@/types'

type JobExpensesPanelProps = {
  jobId: string
  serviceInstanceId?: string | null
}

const formatShortId = (value: string | number | null | undefined) => String(value ?? '').slice(0, 8)

const getPaymentStatusBadge = (status?: Expense['payment_status']) => {
  const key = status || 'unpaid'
  const styles: Record<string, string> = {
    unpaid: 'border-rose-200 bg-rose-50 text-rose-700',
    partial: 'border-amber-200 bg-amber-50 text-amber-700',
    paid: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }

  return (
    <Badge variant="outline" className={styles[key] || styles.unpaid}>
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </Badge>
  )
}

const getBillingBadge = (expense: Expense) => {
  const label = expense.billable
    ? 'Billable'
    : expense.reimbursable
      ? 'Reimbursable'
      : expense.paid_by_company
        ? 'Company Paid'
        : 'Internal'

  const styles: Record<string, string> = {
    Billable: 'border-sky-200 bg-sky-50 text-sky-700',
    Reimbursable: 'border-amber-200 bg-amber-50 text-amber-700',
    'Company Paid': 'border-emerald-200 bg-emerald-50 text-emerald-700',
    Internal: 'border-slate-200 bg-slate-50 text-slate-700',
  }

  return (
    <Badge variant="outline" className={styles[label] || styles.Internal}>
      {label}
    </Badge>
  )
}

export function JobExpensesPanel({ jobId, serviceInstanceId }: JobExpensesPanelProps) {
  const expensesQuery = useQuery({
    queryKey: ['job-expenses', jobId],
    queryFn: () => financeApi.getExpenses({ job_id: jobId, page_size: 200 }),
    enabled: Boolean(jobId),
    staleTime: 30_000,
  })

  const expenses = expensesQuery.data?.results ?? []
  const expensesHref = withServiceInstanceId(
    `/job/expenses?job_id=${encodeURIComponent(jobId)}`,
    serviceInstanceId,
  )

  if (expensesQuery.isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (!expenses.length) {
    return (
      <Empty className="py-10">
        <Receipt className="mb-4 h-10 w-10 text-muted-foreground/60" />
        <p className="text-lg font-medium">No expenses yet</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Record job costs, reimbursements, and supplier payments from the expenses page.
        </p>
        <Button className="mt-4" asChild>
          <Link to={expensesHref}>
            <Receipt className="mr-2 h-4 w-4" />
            Add expense
          </Link>
        </Button>
      </Empty>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border/70">
      <table className="w-full text-sm">
        <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Expense</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Worker / Supplier</th>
            <th className="px-4 py-3">Payment</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Billing</th>
            <th className="px-4 py-3">Date</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense) => (
            <tr key={expense.id} className="border-t border-border/60">
              <td className="px-4 py-3">
                <div className="space-y-0.5">
                  <p className="font-medium text-foreground">{expense.title || 'Untitled expense'}</p>
                  <p className="text-xs text-muted-foreground">#{formatShortId(expense.id)}</p>
                </div>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{expense.category_name || 'Uncategorized'}</td>
              <td className="px-4 py-3 text-muted-foreground">
                {expense.worker_name || expense.supplier_name || 'General'}
              </td>
              <td className="px-4 py-3">
                <div className="space-y-1">
                  {getPaymentStatusBadge(expense.payment_status)}
                  {expense.outstanding_amount != null && Number(expense.outstanding_amount) > 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      Due {formatCurrency(Number(expense.outstanding_amount))}
                    </p>
                  ) : null}
                </div>
              </td>
              <td className="px-4 py-3 font-medium tabular-nums">{formatCurrency(Number(expense.amount || 0))}</td>
              <td className="px-4 py-3">{getBillingBadge(expense)}</td>
              <td className="px-4 py-3 text-muted-foreground">
                {expense.incurred_at ? formatDate(expense.incurred_at) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
